import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowLeft, User, Calendar, MapPin, FileText, Users, Heart, Pencil, X, Save, Mail, Image, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GENDER_LABEL = { 0: 'Féminin', 1: 'Masculin', 2: 'Inconnu', 3: 'Autre' };
const genderLabel  = (g) => GENDER_LABEL[Number(g)] ?? '';

// ── Helpers UI ────────────────────────────────────────────────────────────────

const Info = ({ label, value }) => (
  <div>
    <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>{label} : </span>
    <span style={{ color: 'var(--text-primary)' }}>{value}</span>
  </div>
);

const PersonLink = ({ person }) => {
  if (!person?.handle) return null;
  return (
    <Link to={`/persons/${person.handle}`} className="font-medium hover:underline"
      style={{ color: 'var(--forest)' }}>
      {person.given_name || ''} {person.surname || ''}
    </Link>
  );
};

const FamilySection = ({ title, icon, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wide"
      style={{ color: 'var(--text-muted)' }}>
      {icon} {title}
    </div>
    <div className="pl-1">{children}</div>
  </div>
);

// ── Visionneuse image plein écran ─────────────────────────────────────────────
const LightBox = ({ images, index, onClose }) => {
  const [current, setCurrent] = useState(index);
  const [scale, setScale]     = useState(1);

  const img  = images[current];
  const prev = () => { setCurrent(i => (i - 1 + images.length) % images.length); setScale(1); };
  const next = () => { setCurrent(i => (i + 1) % images.length); setScale(1); };
  const zoomIn  = () => setScale(s => Math.min(s + 0.25, 4));
  const zoomOut = () => setScale(s => Math.max(s - 0.25, 0.25));

  // Clavier : ← → Échap
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Molette pour zoomer
  const handleWheel = (e) => {
    e.preventDefault();
    setScale(s => Math.min(Math.max(s - e.deltaY * 0.001, 0.25), 4));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90"
      onClick={onClose}
    >
      {/* Barre haute */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-10"
        style={{ background: 'rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>
        <span className="text-white/60 text-sm">
          {current + 1} / {images.length}
        </span>
        <span className="text-white text-sm truncate max-w-xs">{img.desc || ''}</span>
        <button onClick={onClose} className="text-white/70 hover:text-white">
          <X size={20} />
        </button>
      </div>

      {/* Zone image */}
      <div
        className="flex items-center justify-center w-full h-full overflow-hidden"
        onClick={onClose}
        onWheel={handleWheel}
      >
        <img
          src={img.url} alt={img.desc || ''}
          onClick={e => e.stopPropagation()}
          style={{
            transform: `scale(${scale})`,
            transition: 'transform 0.15s ease',
            maxWidth: '90vw',
            maxHeight: '80vh',
            objectFit: 'contain',
            cursor: scale > 1 ? 'grab' : 'default',
          }}
        />
      </div>

      {/* Flèche précédente */}
      {images.length > 1 && (
        <button
          onClick={e => { e.stopPropagation(); prev(); }}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/70 rounded-full p-2"
        >
          <ChevronLeft size={28} />
        </button>
      )}

      {/* Flèche suivante */}
      {images.length > 1 && (
        <button
          onClick={e => { e.stopPropagation(); next(); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/70 rounded-full p-2"
        >
          <ChevronRight size={28} />
        </button>
      )}

      {/* Barre basse : zoom */}
      <div className="absolute bottom-4 flex items-center gap-3 px-4 py-2 rounded-full"
        style={{ background: 'rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>
        <button onClick={zoomOut} className="text-white/70 hover:text-white"><ZoomOut size={18} /></button>
        <span className="text-white/60 text-xs w-10 text-center">{Math.round(scale * 100)}%</span>
        <button onClick={zoomIn}  className="text-white/70 hover:text-white"><ZoomIn  size={18} /></button>
      </div>
    </div>
  );
};

// ── Panneau d'édition ─────────────────────────────────────────────────────────
const EditPanel = ({ person, onSave, onCancel }) => {
  const [form, setForm] = useState({
    given_name: person.given_name || '',
    surname:    person.surname    || '',
    birth_date: person.birth_date || '',
    death_date: person.death_date || '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const handleChange = (e) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const payload = {};
      if (form.given_name !== (person.given_name || '')) payload.given_name = form.given_name;
      if (form.surname    !== (person.surname    || '')) payload.surname    = form.surname;
      if (form.birth_date !== (person.birth_date || '')) payload.birth_date = form.birth_date;
      if (form.death_date !== (person.death_date || '')) payload.death_date = form.death_date;
      if (!Object.keys(payload).length) { onCancel(); return; }
      const { data } = await axios.patch(
        `${API_URL}/api/persons/${person.handle}`, payload, { withCredentials: true }
      );
      onSave(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { name: 'given_name', label: 'Prénom',            type: 'text',     hint: '' },
    { name: 'surname',    label: 'Nom',                type: 'text',     hint: '' },
    { name: 'birth_date', label: 'Date de naissance',  type: 'text',     hint: 'j/m/aaaa, m/aaaa ou aaaa' },
    { name: 'death_date', label: 'Date de décès',      type: 'text',     hint: 'j/m/aaaa, m/aaaa ou aaaa' },
  ];

  return (
    <form onSubmit={handleSubmit}
      className="border rounded-sm p-5 mb-4 space-y-4"
      style={{ backgroundColor: 'var(--bg-subtle)', borderColor: 'var(--border)' }}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: 'var(--forest)' }}>
          <Pencil size={15} /> Modifier les informations simples
        </h3>
        <button type="button" onClick={onCancel} style={{ color: 'var(--text-muted)' }}>
          <X size={18} />
        </button>
      </div>
      {error && (
        <div className="text-sm px-3 py-2 border rounded-sm"
          style={{ color: 'var(--terracotta)', borderColor: 'var(--terracotta)',
                   backgroundColor: 'rgba(168,87,60,0.06)' }}>
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(({ name, label, type, hint }) => (
          <div key={name}>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
              {label}
            </label>
            <input name={name} type={type} value={form[name]} onChange={handleChange}
              className="w-full px-3 py-2 text-sm border rounded-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border)',
                       color: 'var(--text-primary)' }} />
            {hint && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{hint}</p>}
          </div>
        ))}
      </div>
      <div className="text-xs px-3 py-2 border rounded-sm flex items-start gap-2"
        style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', backgroundColor: 'var(--bg-card)' }}>
        <Mail size={12} className="shrink-0 mt-0.5" />
        Pour toute modification complexe, utilisez la{' '}
        <Link to="/contact" className="underline" style={{ color: 'var(--forest)' }}>page de contact</Link>.
      </div>
      <div className="flex gap-3 justify-end">
        <button type="button" onClick={onCancel}
          className="px-4 py-2 text-sm border rounded-sm"
          style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)', backgroundColor: 'var(--bg-card)' }}>
          Annuler
        </button>
        <button type="submit" disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white rounded-sm flex items-center gap-2 disabled:opacity-60"
          style={{ backgroundColor: 'var(--forest)' }}>
          <Save size={14} />
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
};

// ── Page principale ───────────────────────────────────────────────────────────
export const PersonDetailPage = () => {
  const { handle } = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();

  const [person,  setPerson]  = useState(null);
  const [medias,  setMedias]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [familyEvents, setFamilyEvents] = useState([]);

  const canEdit = user?.role === 'admin' || user?.role === 'contributor';

  // Chargement de la personne
  useEffect(() => {
    setLoading(true); setEditing(false); setMedias([]);
    axios.get(`${API_URL}/api/persons/${handle}`, { withCredentials: true })
      .then(({ data }) => setPerson(data))
      .catch(() => setPerson(null))
      .finally(() => setLoading(false));
  }, [handle]);

  // Chargement des médias après avoir la personne
  useEffect(() => {
    if (!person?.handle) { setMedias([]); return; }
    axios.get(`${API_URL}/api/media/person/${encodeURIComponent(person.handle)}`,
      { withCredentials: true })
      .then(({ data }) => setMedias(data))
      .catch(() => setMedias([]));
  }, [person?.handle]);

  // Chargement des événements familiaux (mariage, divorce, etc.)
  useEffect(() => {
    if (!person?.json_data) { setFamilyEvents([]); return; }
    const jd = typeof person.json_data === 'string'
      ? JSON.parse(person.json_data) : person.json_data;

    // Handles des familles où la personne est époux/épouse
    const familyHandles = (jd.family_list || [])
      .map(f => typeof f === 'string' ? f : f?.ref).filter(Boolean);

    if (!familyHandles.length) { setFamilyEvents([]); return; }

    const EVENT_TYPES = {
      1:'Mariage', 2:'Mariage civil', 3:'Fiançailles', 4:'Divorce',
      5:'Annulation', 6:'Séparation', 12:'Naissance', 13:'Décès',
    };

    const parseDate = (dateObj) => {
      if (!dateObj || typeof dateObj !== 'object') return '';
      const dv = dateObj.dateval;
      if (Array.isArray(dv) && dv.length >= 3) {
        const [j, m, a] = dv;
        return [j, m, a].filter(Boolean).join('/');
      }
      return dateObj.text || '';
    };

    Promise.all(
      familyHandles.map(fh =>
        axios.get(`${API_URL}/api/families/${fh}`, { withCredentials: true }).catch(() => null)
      )
    ).then(async (famResults) => {
      const allFamEvents = [];
      for (const res of famResults.filter(Boolean)) {
        const fam = res.data;
        const famJd = typeof fam.json_data === 'string'
          ? JSON.parse(fam.json_data) : (fam.json_data || {});
        const eventRefs = (famJd.event_ref_list || []).map(r => r?.ref || r).filter(Boolean);

        const evResults = await Promise.all(
          eventRefs.map(ref =>
            axios.get(`${API_URL}/api/events/${ref}`, { withCredentials: true }).catch(() => null)
          )
        );

        for (const evRes of evResults.filter(Boolean)) {
          const ev = evRes.data;
          const evJd = typeof ev.json_data === 'string'
            ? JSON.parse(ev.json_data) : (ev.json_data || {});
          const typeValue = evJd?.type?.value;

          // Garder seulement les événements familiaux (pas naissance/décès déjà présents)
          if (![1,2,3,4,5,6].includes(typeValue)) continue;

          let place_name = '';
          if (ev.place) {
            try {
              const { data: pl } = await axios.get(`${API_URL}/api/places/${ev.place}`, { withCredentials: true });
              const plJd = typeof pl.json_data === 'string' ? JSON.parse(pl.json_data) : (pl.json_data || {});
              place_name = plJd?.name?.value || pl.title || '';
            } catch { place_name = ''; }
          }

          allFamEvents.push({
            event_type: EVENT_TYPES[typeValue] || evJd?.type?.string || 'Événement familial',
            date_str: parseDate(evJd?.date),
            place_name,
            family_handle: fam.handle,
          });
        }
      }
      setFamilyEvents(allFamEvents);
    });
  }, [person?.handle]);

  if (loading) return (
    <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Chargement…</div>
  );
  if (!person) return (
    <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Personne non trouvée</div>
  );

  const events = [...(person.events || []), ...familyEvents];
  const parents  = person.parents  || [];
  const siblings = person.siblings || [];
  const spouses  = person.spouses  || [];
  const children = person.children || [];
  const hasFamily = parents.length || siblings.length || spouses.length || children.length;

  // Séparer images et autres documents
  const images = medias.filter(m => m.mime?.startsWith('image/'));
  const docs   = medias.filter(m => !m.mime?.startsWith('image/'));

  return (
    <div data-testid="person-detail-page" className="space-y-4">

      {/* Lightbox */}
      {lightbox !== null && (
        <LightBox images={images} index={lightbox} onClose={() => setLightbox(null)} />
      )}

      {/* Retour */}
      <button onClick={() => navigate('/persons')}
        className="flex items-center gap-2 text-sm font-medium"
        style={{ color: 'var(--terracotta)' }}>
        <ArrowLeft size={16} /> Retour à la liste
      </button>

      <div className="border p-6 md:p-8"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>

        {/* ── En-tête ── */}
        <div className="flex items-start gap-5 mb-6">
          {/* Miniature du premier média si disponible */}
          {images.length > 0 ? (
            <button
              onClick={() => setLightbox(0)}
              className="w-16 h-16 shrink-0 overflow-hidden border rounded-sm"
              style={{ borderColor: 'var(--border)' }}>
              <img src={images[0].url} alt={images[0].desc || ''}
                className="w-full h-full object-cover" />
            </button>
          ) : (
            <div className="w-16 h-16 flex items-center justify-center text-2xl font-bold border shrink-0"
              style={{ backgroundColor: 'var(--bg-subtle)', borderColor: 'var(--border)',
                       color: 'var(--forest)' }}>
              {person.given_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}

          <div className="flex-1 min-w-0">
            <h1 className="text-3xl md:text-4xl font-bold mb-1"
              style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
              {person.given_name || ''} {person.surname || ''}
            </h1>
            {person.gramps_id && (
              <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>ID : {person.gramps_id}</p>
            )}
            {person.gender != null && (
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{genderLabel(person.gender)}</p>
            )}
            {(person.birth_date || person.birth_place) && (
              <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                <span className="font-medium">Naissance :</span>{' '}
                {[person.birth_date, person.birth_place].filter(Boolean).join(' — ')}
              </p>
            )}
            {(person.death_date || person.death_place) && (
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                <span className="font-medium">Décès :</span>{' '}
                {[person.death_date, person.death_place].filter(Boolean).join(' — ')}
              </p>
            )}
          </div>

          {canEdit && !editing && (
            <button onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-sm shrink-0"
              style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)',
                       backgroundColor: 'var(--bg-subtle)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--forest)'; e.currentTarget.style.color = 'var(--forest)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
              <Pencil size={14} /> Modifier
            </button>
          )}
        </div>

        {editing && (
          <EditPanel person={person}
            onSave={(updated) => { setPerson(updated); setEditing(false); }}
            onCancel={() => setEditing(false)} />
        )}

        <Accordion type="multiple" defaultValue={['info', 'events', 'family', 'media']} className="space-y-2">

          {/* ── Informations générales ── */}
          <AccordionItem value="info" className="border" style={{ borderColor: 'var(--border)' }}>
            <AccordionTrigger className="px-6 hover:no-underline">
              <div className="flex items-center gap-3">
                <User size={18} style={{ color: 'var(--forest)' }} />
                <span className="font-semibold" style={{ color: 'var(--forest)' }}>Informations générales</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 py-4" style={{ backgroundColor: 'var(--bg-subtle)' }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {person.given_name  && <Info label="Prénom"            value={person.given_name} />}
                {person.surname     && <Info label="Nom"               value={person.surname} />}
                {person.gender != null && <Info label="Genre"          value={genderLabel(person.gender)} />}
                {person.gramps_id   && <Info label="ID Gramps"         value={person.gramps_id} />}
                {person.birth_date  && <Info label="Date de naissance" value={person.birth_date} />}
                {person.birth_place && <Info label="Lieu de naissance" value={person.birth_place} />}
                {person.death_date  && <Info label="Date de décès"     value={person.death_date} />}
                {person.death_place && <Info label="Lieu de décès"     value={person.death_place} />}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* ── Événements ── */}
          {events.length > 0 && (
            <AccordionItem value="events" className="border" style={{ borderColor: 'var(--border)' }}>
              <AccordionTrigger className="px-6 hover:no-underline">
                <div className="flex items-center gap-3">
                  <Calendar size={18} style={{ color: 'var(--terracotta)' }} />
                  <span className="font-semibold" style={{ color: 'var(--forest)' }}>
                    Événements ({events.length})
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 py-4" style={{ backgroundColor: 'var(--bg-subtle)' }}>
                <div className="space-y-4">
                  {events.map((ev, idx) => (
                    <div key={idx} className="border-l-2 pl-4" style={{ borderColor: 'var(--terracotta)' }}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                          {ev.event_type || ev.description || 'Événement'}
                        </span>
                        {ev.date_str && (
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{ev.date_str}</span>
                        )}
                      </div>
                      {ev.place_name && (
                        <div className="flex items-center gap-1 text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                          <MapPin size={12} style={{ color: 'var(--terracotta)' }} />
                          {ev.place_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* ── Famille ── */}
          {hasFamily > 0 && (
            <AccordionItem value="family" className="border" style={{ borderColor: 'var(--border)' }}>
              <AccordionTrigger className="px-6 hover:no-underline">
                <div className="flex items-center gap-3">
                  <Users size={18} style={{ color: 'var(--forest)' }} />
                  <span className="font-semibold" style={{ color: 'var(--forest)' }}>Famille</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 py-4" style={{ backgroundColor: 'var(--bg-subtle)' }}>
                <div className="space-y-5">
                  {parents.length > 0 && (
                    <FamilySection title="Parents"
                      icon={<Heart size={13} style={{ color: 'var(--terracotta)' }} />}>
                      <div className="space-y-1">
                        {parents.map((p, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <span className="w-10 shrink-0 text-xs" style={{ color: 'var(--text-muted)' }}>
                              {p.relation}
                            </span>
                            <PersonLink person={p} />
                          </div>
                        ))}
                      </div>
                    </FamilySection>
                  )}
                  {spouses.length > 0 && (
                    <FamilySection title="Conjoint(s)"
                      icon={<Heart size={13} style={{ color: 'var(--terracotta)' }} />}>
                      <div className="flex flex-wrap gap-3">
                        {spouses.map((s, i) => <PersonLink key={i} person={s} />)}
                      </div>
                    </FamilySection>
                  )}
                  {siblings.length > 0 && (
                    <FamilySection title={`Frères & sœurs (${siblings.length})`}
                      icon={<Users size={13} style={{ color: 'var(--forest)' }} />}>
                      <div className="flex flex-wrap gap-3">
                        {siblings.map((s, i) => <PersonLink key={i} person={s} />)}
                      </div>
                    </FamilySection>
                  )}
                  {children.length > 0 && (
                    <FamilySection title={`Enfants (${children.length})`}
                      icon={<Users size={13} style={{ color: 'var(--forest)' }} />}>
                      <div className="flex flex-wrap gap-3">
                        {children.map((c, i) => <PersonLink key={i} person={c} />)}
                      </div>
                    </FamilySection>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* ── Médias ── */}
          {medias.length > 0 && (
            <AccordionItem value="media" className="border" style={{ borderColor: 'var(--border)' }}>
              <AccordionTrigger className="px-6 hover:no-underline">
                <div className="flex items-center gap-3">
                  <Image size={18} style={{ color: 'var(--forest)' }} />
                  <span className="font-semibold" style={{ color: 'var(--forest)' }}>
                    Médias ({medias.length})
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 py-4" style={{ backgroundColor: 'var(--bg-subtle)' }}>

                {/* Galerie photos */}
                {images.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold uppercase tracking-wide mb-3"
                      style={{ color: 'var(--text-muted)' }}>
                      Photos ({images.length})
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {images.map((img, i) => (
                        <button key={i}
                          onClick={() => setLightbox(i)}
                          className="group relative aspect-square overflow-hidden border rounded-sm"
                          style={{ borderColor: 'var(--border)' }}>
                          <img src={img.url} alt={img.desc || ''}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                          {/* Overlay au survol */}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
                            <ZoomIn size={22} className="text-white" />
                          </div>
                          {img.desc && (
                            <div className="absolute bottom-0 left-0 right-0 px-2 py-1 text-xs text-white truncate"
                              style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                              {img.desc}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents */}
                {docs.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-3"
                      style={{ color: 'var(--text-muted)' }}>
                      Documents ({docs.length})
                    </p>
                    <div className="space-y-2">
                      {docs.map((doc, i) => (
                        <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 px-3 py-2 border rounded-sm hover:underline text-sm"
                          style={{ borderColor: 'var(--border)', color: 'var(--forest)',
                                   backgroundColor: 'var(--bg-card)' }}>
                          <FileText size={15} style={{ color: 'var(--terracotta)', flexShrink: 0 }} />
                          <span className="truncate">{doc.desc || doc.path}</span>
                          <span className="ml-auto text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>
                            {doc.mime}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          )}

          {/* ── Données brutes ── */}
          {person.json_data && (
            <AccordionItem value="json" className="border" style={{ borderColor: 'var(--border)' }}>
              <AccordionTrigger className="px-6 hover:no-underline">
                <div className="flex items-center gap-3">
                  <FileText size={18} style={{ color: 'var(--forest)' }} />
                  <span className="font-semibold" style={{ color: 'var(--forest)' }}>Données complémentaires</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 py-4" style={{ backgroundColor: 'var(--bg-subtle)' }}>
                <pre className="text-xs overflow-auto max-h-96" style={{ color: 'var(--text-secondary)' }}>
                  {JSON.stringify(person.json_data, null, 2)}
                </pre>
              </AccordionContent>
            </AccordionItem>
          )}

        </Accordion>

        {/* Lien contact */}
        <div className="mt-6 pt-4 border-t text-sm flex items-center gap-2"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <Mail size={14} />
          <span>Pour signaler une erreur ou demander une modification complexe :</span>
          <Link to="/contact" className="font-medium underline hover:no-underline"
            style={{ color: 'var(--forest)' }}>
            page de contact
          </Link>
        </div>
      </div>
    </div>
  );
};